<template>
  <div :class="['relative', $style.field]">
    <div>
      <span
        :class="[
          $style.checkbox,
          {
            [$style.isChecked]: modelValue,
            isError,
            isDisabled
          }
        ]"
        aria-hidden="true"
      >
        <span :class="$style.check"></span>
      </span>
      <input
        ref="input"
        v-bind="$attrs"
        v-uid
        class="sr-only"
        :checked="modelValue"
        :disabled="isDisabled"
        type="checkbox"
        @input="updateValue"
      />
      <label
        :for="input?.id"
        :class="[
          'min-h-[var(--checkbox-size)]] font-mono relative inline-flex w-full pl-[calc(var(--checkbox-size)+12px)] leading-[var(--checkbox-size)]',
          isLabelVisible ? 'cursor-pointer' : 'sr-only',
          {
            isError,
            isDisabled
          }
        ]"
        v-html="label"
      ></label>
    </div>
    <div v-if="isError" class="mt-3 flex w-full text-sm text-red">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script lang="ts">
export default {
  inheritAttrs: false
}
</script>

<script setup lang="ts">
interface InputProps {
  label: string
  isLabelVisible?: boolean
  modelValue?: boolean
  isDisabled?: boolean
  isError?: boolean
  errorMessage?: string
}

withDefaults(defineProps<InputProps>(), {
  isLabelVisible: true,
  modelValue: false,
  type: 'text',
  isDisabled: false,
  isError: false
})

const emit = defineEmits(['update:modelValue'])

const updateValue = (e: Event) => {
  emit('update:modelValue', (e.target as HTMLInputElement).checked)
}

const input = ref<null | HTMLElement>(null)
</script>

<style lang="postcss" module>
.field {
  --checkbox-size: 20px;
  --checkbox-background: theme('colors.dark');
  --checkbox-color: theme('colors.white');
  --checkbox-color-isError: theme('colors.red');
  --checkbox-color-isDisabled: theme('colors.disabled');
}

.checkbox {
  position: absolute;
  left: 0;
  top: 0;
  width: var(--checkbox-size);
  height: var(--checkbox-size);
  background: var(--checkbox-background);
  border-radius: 100%;
}

.check {
  --check-size: 8px;
  appearance: none;
  border: none;
  border-radius: 100%;
  backface-visibility: hidden;
  width: var(--check-size);
  height: var(--check-size);
  background: var(--checkbox-color);
  display: block;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) scale(0);
  transition: 150ms ease-out;
}

/*  States  */

.isChecked {
  .check {
    transform: translate(-50%, -50%) scale(1);
  }
}

.isError {
  border-color: var(--checkbox-color-isError);

  .check {
    background: var(--checkbox-color-isError);
  }
}

.isDisabled {
  border-color: var(--checkbox-color-isDisabled);

  ~ label {
    cursor: default;
  }

  .check {
    background: var(--checkbox-color-isDisabled);
  }
}
</style>
